import React, { useEffect, useState } from "react";
import { useSelector } from 'react-redux';
import {
    Box,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    TextField,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    TablePagination,
    IconButton,
    Divider,
    TableSortLabel,
    MenuItem
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import HttpService from "../../services/httpService";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { Link } from 'react-router-dom';
import moment from 'moment';

const RNCRevivalReport = () => {
    const { userDetails } = useSelector((state) => state.user);

    const [data, setData] = useState([]);
    const [grandTotal, setGrandTotal] = useState({ total: 0, revived: 0 });

    const [grandTotalLeads, setGrandTotalLeads] = useState([]);
    const [grandRevivedLeads, setGrandRevivedLeads] = useState([]);

    const [loading, setLoading] = useState(false);

    const [filters, setFilters] = useState({
        startDate: "",
        endDate: "",
        dateType: "leadAssignedDate"
    });

    const [modalOpen, setModalOpen] = useState(false);
    const [modalLeads, setModalLeads] = useState([]);

    const [search, setSearch] = useState("");

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [orderBy, setOrderBy] = useState("lead_no");
    const [order, setOrder] = useState("asc");

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {

            setLoading(true);

            const params = {
                startDate: filters.startDate,
                endDate: filters.endDate,
                dateType: filters.dateType,
                userExpCenter: userDetails.experienceCenterId
            };

            const res = await HttpService.getRNCRevival(params);

            setData(res.data.experienceCenters || []);
            setGrandTotal(res.data.grandTotal || { total: 0, revived: 0 });

            setGrandTotalLeads(res.data.grandTotal?.totalLeads || []);
            setGrandRevivedLeads(res.data.grandTotal?.revivedLeads || []);
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    };

    const openModal = (leads) => {
        setModalLeads(leads || []);
        setModalOpen(true);
    };

    const handleSort = (field) => {

        const isAsc = orderBy === field && order === "asc";

        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(field);
        setPage(0);
    };

    const sortedLeads = [...modalLeads].sort((a, b) => {

        let valueA = a[orderBy];
        let valueB = b[orderBy];

        // Handle date fields properly
        if (["createdAt", "leadAssignedDate", "revivedDate"].includes(orderBy)) {
            valueA = valueA ? new Date(valueA).getTime() : 0;
            valueB = valueB ? new Date(valueB).getTime() : 0;
        }

        // Handle string fields
        if (typeof valueA === "string") valueA = valueA.toLowerCase();
        if (typeof valueB === "string") valueB = valueB.toLowerCase();

        if (valueA < valueB) return order === "asc" ? -1 : 1;
        if (valueA > valueB) return order === "asc" ? 1 : -1;

        return 0;
    });

    const filteredLeads = sortedLeads.filter((lead) =>
        lead.customerName?.toLowerCase().includes(search.toLowerCase()) ||
        lead.customerEmail?.toLowerCase().includes(search.toLowerCase()) ||
        String(lead.lead_no).includes(search)
    );

    const exportSummaryExcel = () => {
        const formattedData = data.map((row, index) => ({
            "Sl No": index + 1,
            "Experience Center": row?.experienceCenterName || "-",
            "RNC Campaign Sent": row?.total || 0,
            "RNC Revived": row?.revived || 0
        }));

        // Add Grand Total row
        formattedData.push({
            "Sl No": data.length + 1,
            "Experience Center": "Grand Total",
            "RNC Campaign Sent": grandTotal.total || 0,
            "RNC Revived": grandTotal.revived || 0
        });

        const worksheet = XLSX.utils.json_to_sheet(formattedData);
        const workbook = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(workbook, worksheet, "RNC Summary");

        const excelBuffer = XLSX.write(workbook, {
            bookType: "xlsx",
            type: "array"
        });

        const file = new Blob([excelBuffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        });

        saveAs(file, "RNC_Summary.xlsx");
    };

    const exportExcel = () => {
        const formattedData = filteredLeads.map((lead, index) => ({
            "Sl.No": index + 1,
            "Lead No": lead?.lead_no || "NA",
            "Customer Name": lead?.customerName || "-",
            "Email": lead?.customerEmail || "-",
            "Phone": lead?.customerContactNo || "-",
            "Current Stage": lead?.currentStage || "-",
            "Experience Center": lead?.experienceCenter || "-",
            "Source Of Lead": lead?.sourceOfLead || "-",
            "Created At": lead?.createdAt
                ? moment(lead.createdAt).format("DD-MM-YYYY")
                : "-",
            "Lead Assigned Date": lead?.leadAssignedDate
                ? moment(lead.leadAssignedDate).format("DD-MM-YYYY")
                : "-",
            "Revived At": lead?.revivedDate
                ? moment(lead.revivedDate).format("DD-MM-YYYY")
                : "-",
            "Customer Response": lead?.rncCustomerResponseBotWot || "No Response Yet",
            "Sales Person": lead?.salesUser || "NA"
        }));

        const worksheet = XLSX.utils.json_to_sheet(formattedData);

        const workbook = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(workbook, worksheet, "RNC Leads");

        const excelBuffer = XLSX.write(workbook, {
            bookType: "xlsx",
            type: "array"
        });

        const file = new Blob([excelBuffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        });

        saveAs(file, "RNC_Leads.xlsx");
    };

    const formatDate = (date) => {

        if (!date) return "-"; // handle null / undefined / empty

        const d = new Date(date);
        // extra safety (invalid date case)
        if (isNaN(d.getTime())) return "-";

        const day = String(d.getDate()).padStart(2, "0");
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const year = d.getFullYear();

        return `${day}-${month}-${year}`;
    };

    const clearFilter = async () => {
        // Reset filters first
        setFilters({ startDate: '', endDate: '', dateType: "leadAssignedDate" });

        // Fetch default data without date filters
        try {
            setLoading(true);
            const params = {
                startDate: "", // empty
                endDate: "",   // empty
                dateType: "leadAssignedDate",
                userExpCenter: userDetails.experienceCenterId
            };

            const res = await HttpService.getRNCRevival(params);

            setData(res.data.experienceCenters || []);
            setGrandTotal(res.data.grandTotal || { total: 0, revived: 0 });

            setGrandTotalLeads(res.data.grandTotal?.totalLeads || []);
            setGrandRevivedLeads(res.data.grandTotal?.revivedLeads || []);
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    };

    return (

        <Box p={4}>
            <Box sx={{ width: "85%", margin: "0 auto" }}>

                {/* Heading */}
                <Typography
                    variant="h4"
                    style={{
                        fontWeight: 700,
                        marginBottom: 25,
                        color: "#2c3e50"
                    }}
                >
                    RNC Revival Report
                </Typography>


                {/* Filters */}

                <Paper elevation={3} style={{ padding: 20, marginBottom: 25 }}>
                    <Box
                        display="flex"
                        flexWrap="wrap"
                        alignItems="center"
                        justifyContent="space-between"
                        gap={2}
                    >
                        <Box display="flex" flexWrap="wrap" alignItems="center">
                            <TextField
                                select
                                label="Date Type"
                                variant="outlined"
                                size="small"
                                value={filters.dateType}
                                onChange={(e) =>
                                    setFilters({ ...filters, dateType: e.target.value })
                                }
                                style={{ width: 210, marginRight: 10 }}
                            >
                                <MenuItem value="createdAt">Created At</MenuItem>
                                <MenuItem value="leadAssignedDate">Lead Assigned Date</MenuItem>
                            </TextField>

                            <TextField
                                type="date"
                                label={
                                    filters.dateType === "createdAt"
                                        ? "Created From Date"
                                        : "Assigned From Date"
                                }
                                variant="outlined"
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                value={filters.startDate}
                                onChange={(e) =>
                                    setFilters({ ...filters, startDate: e.target.value })
                                }
                                style={{ marginRight: 10 }}
                            />

                            <TextField
                                type="date"
                                label={
                                    filters.dateType === "createdAt"
                                        ? "Created To Date"
                                        : "Assigned To Date"
                                }
                                variant="outlined"
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                value={filters.endDate}
                                onChange={(e) =>
                                    setFilters({ ...filters, endDate: e.target.value })
                                }
                                style={{ marginRight: 10 }}
                            />

                            <Button
                                variant="contained"
                                color="primary"
                                onClick={fetchData}
                                style={{ marginRight: 10 }}
                            >
                                Apply
                            </Button>

                            <Button
                                variant="outlined"
                                color="secondary"
                                onClick={clearFilter}
                            >
                                Clear
                            </Button>
                        </Box>

                        <Box>
                            <Button
                                variant="contained"
                                style={{
                                    background: "#FFA726",
                                    color: "#fff",
                                    height: 40
                                }}
                                onMouseOver={(e) =>
                                    (e.currentTarget.style.background = "#FB8C00")
                                }
                                onMouseOut={(e) =>
                                    (e.currentTarget.style.background = "#FFA726")
                                }
                                onClick={exportSummaryExcel}
                            >
                                Export Summary
                            </Button>
                        </Box>
                    </Box>
                </Paper>


                {/* Main Table */}

                <Paper elevation={3}>

                    {loading ? (
                        <Box textAlign="center" p={5}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <TableContainer style={{ maxHeight: 700 }}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>

                                        <TableCell
                                            align="center"
                                            style={{
                                                background: "#f0f3f7",
                                                fontWeight: "bold"
                                            }}
                                        >
                                            Sl No
                                        </TableCell>

                                        <TableCell
                                            align="center"
                                            style={{
                                                background: "#f0f3f7",
                                                fontWeight: "bold"
                                            }}
                                        >
                                            Experience Center
                                        </TableCell>

                                        <TableCell
                                            align="center"
                                            style={{
                                                background: "#f0f3f7",
                                                fontWeight: "bold"
                                            }}
                                        >
                                            RNC Campaign Sent
                                        </TableCell>

                                        <TableCell
                                            align="center"
                                            style={{
                                                background: "#f0f3f7",
                                                fontWeight: "bold"
                                            }}
                                        >
                                            RNC Revived
                                        </TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {data.map((row, index) => (
                                        <TableRow key={row.experienceCenterId} hover>
                                            <TableCell align="center">
                                                {index + 1}
                                            </TableCell>
                                            <TableCell align="center">
                                                {row.experienceCenterName}
                                            </TableCell>
                                            <TableCell align="center">
                                                <Button
                                                    variant="outlined"
                                                    color="primary"
                                                    style={{
                                                        textDecoration: "underline"
                                                    }}
                                                    onClick={() => openModal(row.totalLeads)}
                                                >
                                                    {row.total}
                                                </Button>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Button
                                                    variant="contained"
                                                    style={{
                                                        background: "#2ecc71",
                                                        color: "#fff",
                                                        textDecoration: "underline"
                                                    }}
                                                    onClick={() => openModal(row.revivedLeads)}
                                                >
                                                    {row.revived}
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}

                                    {/* Grand Total */}

                                    <TableRow style={{ background: "#fafafa" }}>
                                        <TableCell align="center">
                                            <b>{data.length + 1}</b>
                                        </TableCell>

                                        <TableCell align="center">
                                            <b>Grand Total</b>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Button
                                                variant="outlined"
                                                color="primary"
                                                style={{
                                                    textDecoration: "underline"
                                                }}
                                                onClick={() => openModal(grandTotalLeads)}
                                            >
                                                <b>{grandTotal.total}</b>
                                            </Button>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Button
                                                variant="contained"
                                                style={{
                                                    background: "#27ae60",
                                                    color: "#fff",
                                                    textDecoration: "underline"
                                                }}
                                                onClick={() => openModal(grandRevivedLeads)}
                                            >
                                                <b>{grandTotal.revived}</b>
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Paper>
            </Box>


            {/* Leads Modal */}

            <Dialog
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                fullWidth
                maxWidth="xl"
            >
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between">
                        <Typography variant="h6">
                            Leads
                        </Typography>

                        <IconButton onClick={() => setModalOpen(false)}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <Divider />

                <DialogContent>
                    <Box display="flex" justifyContent="space-between" mb={2}>
                        <TextField
                            label="Search"
                            variant="outlined"
                            size="small"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />

                        <Button
                            variant="contained"
                            color="primary"
                            onClick={exportExcel}
                        >
                            Export Excel
                        </Button>

                    </Box>
                    <TableContainer style={{ maxHeight: "70vh", overflow: "auto" }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow style={{ background: "#f0f3f7" }}>
                                    <TableCell style={{ background: "#f0f3f7" }}><b>SL No</b></TableCell>
                                    <TableCell style={{ background: "#f0f3f7" }}>
                                        <TableSortLabel
                                            active={orderBy === "lead_no"}
                                            direction={order}
                                            onClick={() => handleSort("lead_no")}
                                        >
                                            Lead No
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell style={{ background: "#f0f3f7" }}>
                                        <TableSortLabel
                                            active={orderBy === "customerName"}
                                            direction={order}
                                            onClick={() => handleSort("customerName")}
                                        >
                                            Customer Name
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell style={{ background: "#f0f3f7" }}>Email</TableCell>
                                    <TableCell style={{ background: "#f0f3f7" }}>Phone Number</TableCell>
                                    <TableCell style={{ background: "#f0f3f7" }}>Current Stage</TableCell>
                                    <TableCell style={{ background: "#f0f3f7" }}>Experience Center</TableCell>
                                    <TableCell style={{ background: "#f0f3f7" }}>Source Of Lead</TableCell>
                                    <TableCell style={{ background: "#f0f3f7" }}>
                                        <TableSortLabel
                                            active={orderBy === "createdAt"}
                                            direction={order}
                                            onClick={() => handleSort("createdAt")}
                                        >
                                            Created At
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell style={{ background: "#f0f3f7" }}>
                                        <TableSortLabel
                                            active={orderBy === "leadAssignedDate"}
                                            direction={order}
                                            onClick={() => handleSort("leadAssignedDate")}
                                        >
                                            Lead Assigned Date
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell style={{ background: "#f0f3f7" }}>
                                        <TableSortLabel
                                            active={orderBy === "revivedDate"}
                                            direction={order}
                                            onClick={() => handleSort("revivedDate")}
                                        >
                                            Revived At
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell style={{ background: "#f0f3f7" }}>Customer Response</TableCell>
                                    <TableCell style={{ background: "#f0f3f7" }}>SE</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {filteredLeads
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((lead, index) => (
                                        <TableRow key={lead._id} hover>
                                            <TableCell>
                                                {page * rowsPerPage + index + 1}
                                            </TableCell>
                                            <TableCell>
                                                {lead?.lead_no ? (
                                                    <Link
                                                        to={`/project-logs/${lead._id}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{
                                                            color: "#1C375D",
                                                            textDecoration: "underline",
                                                            textAlign: "center"
                                                        }}
                                                    >
                                                        {lead.lead_no}
                                                    </Link>
                                                ) : (
                                                    "NA"
                                                )}
                                            </TableCell>
                                            <TableCell>{lead?.customerName}</TableCell>
                                            <TableCell>{lead?.customerEmail}</TableCell>
                                            <TableCell>{lead?.customerContactNo}</TableCell>
                                            <TableCell>{lead?.currentStage}</TableCell>
                                            <TableCell>{lead?.experienceCenter}</TableCell>
                                            <TableCell>{lead?.sourceOfLead}</TableCell>
                                            <TableCell>{formatDate(lead?.createdAt)}</TableCell>
                                            <TableCell>{formatDate(lead?.leadAssignedDate)}</TableCell>
                                            <TableCell>{formatDate(lead?.revivedDate)}</TableCell>
                                            <TableCell>{lead?.rncCustomerResponseBotWot || "No Response Yet"}</TableCell>
                                            <TableCell>{lead?.salesUser || "NA"}</TableCell>
                                        </TableRow>
                                    ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        component="div"
                        count={filteredLeads.length}
                        page={page}
                        onPageChange={(e, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value));
                            setPage(0);
                        }}
                    />
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default RNCRevivalReport;